const { useEffect, useState, useRef } = React;

function About() {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.3 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    return (
        <section id="about" ref={sectionRef} className="section-shell relative py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-transparent overflow-hidden">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-full h-1/3 bg-gradient-to-b from-blue-50/80 to-transparent"></div>

            <div className="max-w-6xl mx-auto relative z-10">
                {/* Section Header */}
                <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="section-kicker mx-auto mb-4 w-fit">About</div>
                    <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-gray-900 section-title">
                        What is <span className="bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">BEASMAR?</span>
                    </h2>
                    <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto"></div>
                </div>

                {/* Two Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                    {/* Text Content */}
                    <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                        <p className="text-lg sm:text-xl text-gray-700 mb-6 leading-relaxed">
                            BEASMAR is a streamlined marketplace for discovering homes and booking inspections with verified realtors.
                        </p>
                        <p className="text-base sm:text-lg text-gray-600 mb-8 leading-relaxed">
                            Realtors publish listings with clear media and availability, while clients search, compare, and book viewings without the usual back-and-forth.
                        </p>

                        {/* Feature List */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {[
                                'Instant property discovery across multiple listings',
                                'Real-time availability and appointment booking',
                                'Verified realtor profiles and secure transactions',
                                'Smart filters for personalized property search'
                            ].map((feature, index) => (
                                <div key={index} className="flex items-start gap-3">
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-blue-600 to-blue-400 flex items-center justify-center mt-1">
                                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <span className="text-gray-700 font-medium">{feature}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Illustration Placeholder */}
                    <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                        <div className="relative">
                            {/* Glowing Background */}
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-blue-400/10 rounded-3xl blur-2xl"></div>

                            {/* Card */}
                            <div className="relative surface-card p-8 sm:p-12">
                                <div className="space-y-8">
                                    {/* Top Section */}
                                    <div className="flex gap-4">
                                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600/20 to-blue-400/10 flex items-center justify-center border border-blue-300/30">
                                            <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M10.5 1.5H5.75A2.25 2.25 0 003.5 3.75v12.5A2.25 2.25 0 005.75 18.5h8.5a2.25 2.25 0 002.25-2.25V6.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                            </svg>
                                        </div>
                                        <div>
                                            <div className="text-sm font-semibold text-blue-600 mb-1">Smart Discovery</div>
                                            <div className="text-gray-600 text-sm">Find properties tailored to your needs</div>
                                        </div>
                                    </div>

                                    {/* Middle Section */}
                                    <div className="flex gap-4">
                                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600/20 to-blue-400/10 flex items-center justify-center border border-blue-300/30">
                                            <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 8a1 1 0 112 0V6a1 1 0 11-2 0v2z" fillRule="evenodd" clipRule="evenodd"/>
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="text-sm font-semibold text-blue-600 mb-1">Instant Booking</div>
                                            <div className="text-gray-600 text-sm">Schedule viewings in seconds</div>
                                        </div>
                                    </div>

                                    {/* Bottom Section */}
                                    <div className="flex gap-4">
                                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600/20 to-blue-400/10 flex items-center justify-center border border-blue-300/30">
                                            <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v.5a1.5 1.5 0 001.5 1.5h5a1.5 1.5 0 001.5-1.5V15z" fillRule="evenodd" clipRule="evenodd"/>
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="text-sm font-semibold text-blue-600 mb-1">Verified Realtors</div>
                                            <div className="text-gray-600 text-sm">Connect with trusted professionals</div>
                                        </div>
                                    </div>

                                    <div className="pt-2">
                                        <a href="#pricing" className="btn-secondary w-full justify-center">
                                            View pricing for Realtors
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default About;

